import { SummaryMode } from './types'
import type { TimeSeriesDataPoint } from './types'

/**
 * A series' value at a hovered day, as the tooltip / data table presents it:
 * either a displayable `value`, or `noData`. Only a point observed on the exact
 * day/bucket reads as a `value`; a null (computed / missing / interpolated) point
 * reads as "No data available", matching the greyed no-data line — there is no
 * "carried forward / as of X" value shown for a primary series. A cumulative
 * comparison overlay may still carry its level forward (see `classifyPoint`'s
 * `carryForward` option).
 */
export type ClassifiedPoint =
  | { kind: 'value'; value: number; originalDate?: string }
  | { kind: 'noData' }

interface ClassifiableSeries {
  data: TimeSeriesDataPoint[]
  summaryMode?: SummaryMode
}

/**
 * Classifies a series' point at `timestampMs` for the tooltip / data table.
 *
 * - A point whose `source` is `computed` or `missing` (a null gap) → `noData`.
 * - **Default (exact day):** a `value` only when a point is observed on this exact
 *   day/bucket; a null sample → `noData` (a **Total** fills a missing same-day
 *   count as `0`, matching the bar chart). A greyed no-data day therefore reads
 *   "No data available" rather than the last level carried forward.
 * - **`{ carryForward: true }`:** for a cumulative (Latest) **comparison** overlay
 *   only — carries the latest non-null point on or before the timestamp forward, so
 *   the overlay's level persists across empty buckets instead of dropping out of
 *   the tooltip. Ignored for Total series.
 *
 * Pure and types-only so it can be unit-tested in isolation.
 */
export function classifyPoint(
  series: ClassifiableSeries,
  timestampMs: number,
  options?: { carryForward?: boolean },
): ClassifiedPoint {
  const pointAtDay = series.data.find(
    (p) => new Date(p.date).getTime() === timestampMs,
  )

  // A null computed/missing point is the greyed "no data" case — authoritative
  // when the source label is present.
  if (pointAtDay?.source === 'computed' || pointAtDay?.source === 'missing') {
    return { kind: 'noData' }
  }

  const isTotal = series.summaryMode === SummaryMode.TOTAL

  // Carry-forward: the non-null point with the greatest timestamp still <= target.
  // Opt-in and Latest-only — used for a cumulative comparison overlay whose level
  // must persist across empty buckets. A primary series never carries forward: a
  // day with no observed value is a genuine gap (see the exact-day path below).
  if (options?.carryForward && !isTotal) {
    let best: { value: number; originalDate?: string } | null = null
    let bestTs = -Infinity
    for (const point of series.data) {
      if (point.value == null) continue
      const ts = new Date(point.date).getTime()
      if (ts <= timestampMs && ts >= bestTs) {
        bestTs = ts
        best = { value: point.value, originalDate: point.originalDate }
      }
    }
    return best ? { kind: 'value', ...best } : { kind: 'noData' }
  }

  // Exact-day resolution (default): a value only when observed on this exact
  // day/bucket, else no data.
  if (!pointAtDay) return { kind: 'noData' }
  if (pointAtDay.value == null) {
    // A Total fills a missing same-day count as 0 (a gap is "no bar", matching the
    // bar chart); any other series treats a null sample as no data on this day.
    if (isTotal) {
      return { kind: 'value', value: 0, originalDate: pointAtDay.originalDate }
    }
    return { kind: 'noData' }
  }
  return {
    kind: 'value',
    value: pointAtDay.value,
    originalDate: pointAtDay.originalDate,
  }
}
